import { useState, useRef, useEffect } from 'react';
import { useRequest } from 'ahooks';
import moment from 'moment';

import ChooseDate from "../../components/ChooseDate";
import { useTheme } from '../../theme/ThemeProvider';
import { applyHighchartsTheme } from '../../utils/highchartsTheme';

const Chart = ({ data = [] }) => {
  const containerRef = useRef();
  const { theme } = useTheme();

  useEffect(() => {
    if (data) {
      const litChartData = data.map(v => {
        return { y: Number(v.trade_amount), formattedValue: v.trade_amount, dt: v.date };
      });
      const dateArr = data.map(v => {
        return v.date
      })
      window.Highcharts.setOptions({
        lang: {
          numericSymbols: [' k', ' M', ' B', ' T', ' P', ' E'],
        },
      });

      function totalFormat(params) {
        if (!params) return
        const str = params.toString();
        const reg = str.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g;
        return str.replace(reg, '$1,');
      }

      var chart = new window.Highcharts.chart(applyHighchartsTheme({
        chart: {
          renderTo: containerRef.current,
          zoomType: 'x',
          resetZoomButton: {
            position: {
              align: 'left', // right by default
              verticalAlign: 'top',
              //x: -55,
              //y: 10
              x: 10,
              y: 10,
            },
            relativeTo: 'plot',
          },
        },
        title: {
          useHTML: true,
          text: ' MRC-20 Daily Token Transfer Chart ',
        },
        subtitle: {
          text: document.ontouchstart === undefined ? `Source: ${window.location.host}<br>Click and drag in the plot area to zoom in` : 'Pinch the chart to zoom in',
        },
        xAxis: {
          type: 'category',
          categories: dateArr
        },
        yAxis: {
          title: {
            text: ' MRC-20 Daily Token Transfer Count per Day ',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          area: {
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, window.Highcharts.getOptions().colors[0]],
                [1, window.Highcharts.Color(window.Highcharts.getOptions().colors[0]).setOpacity(0).get('rgba')],
              ],
            },
            marker: {
              radius: 2,
            },
            lineWidth: 1,
            states: {
              hover: {
                lineWidth: 1,
              },
            },
            threshold: null,
          },
        },
        credits: {
          enabled: false,
        },

        tooltip: {
          formatter: function () {
            return (
              '<span style="font-size:10px">' +
              this.point.dt +
              '</span><br><table><tr><td style="padding:0">' +
              '[ <span style="color:' +
              this.series.color +
              '">Total Token Transfer : </a></span><b>' +
              totalFormat(this.point.formattedValue) +
              '</b> ]<br>' +
              '</td></tr></table>'
            );
          },
        },
        series: [
          {
            type: 'line',
            name: 'Daily Transaction Count',
            data: litChartData,
          },
        ],
      }));
    }
  }, [data, theme]);

  return <div className="content" ref={containerRef} style={{ height: '550px', minWidth: '310px', overflow: 'hidden' }} data-highcharts-chart="0"></div>;
}

export default function TokenTransfer() {
  const [timeType, setTimeType] = useState('daily');
  const initDate = {
    time_type: 'daily',
    start_date: '',
    end_date: '',
    // start_date: moment().subtract(1, 'days').subtract(3, "months").format('YYYY-MM-DD'),
    // end_date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
  }
  
  const [state, setState] = useState(initDate);

  const dailyTradeChartRequest = useRequest(
    body => ({
      url: '/blockBrowser/resources/chartStats/blockChainData/tokenTradeChart',
      method: 'post',
      body: JSON.stringify(body),
    }),
    { manual: true },
  )

  useEffect(() => {
    dailyTradeChartRequest.run(state)
  }, [state])

  const data = dailyTradeChartRequest.data || [];

  const selectType = (type) => {
    setTimeType(type)
    setState({ ...initDate, time_type: type })
  }

  const dateChange = (value, dateString) => {
    if(dateString[0] && dateString[1]){
      setState({ ...initDate, start_date: dateString[0], end_date: dateString[1] })
    }else{
      setState({ ...initDate})
    }
  }

  const downLoadUrl = timeType == 'daily' ? `timeType=${state.time_type}&startDate=${state.start_date}&endDate=${state.end_date}` : `timeType=${state.time_type}`

  return (
    <main id="content" role="main">
      <div className="container">
        <div className="d-md-flex justify-content-between align-items-center border-bottom py-3">
          <div className="mb-1 mb-md-0">
            <h1 className="h4 mb-0">
              <span className="d-block">MRC-20 Daily Token Transfer Chart</span>
            </h1>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb small p-0 mb-0">
              <li className="breadcrumb-item">
                <a href="/charts">Charts &amp; Stats</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/charts#blockchainData" id="ContentPlaceHolder1_aChartRegion">
                  Blockchain Data
                </a>
              </li>
              <li className="breadcrumb-item active">MRC-20 Daily Token Transfer Chart</li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="container">
        <div className="card mt-3 mb-4">
          <div className="card-body">
            <span>
              The chart shows the number of MRC-20 tokens transferred daily.
              {/* The chart shows the total number of contracts verified daily. Check out the 500 most recent <a href="/contractsVerified">verified contracts!</a> */}
            </span>
          </div>
        </div>
        <div className="card">
          <div className="d-md-flex space-top-1 align-items-center">
            <ChooseDate
              timeType={timeType}
              bodyDate={state}
              selectType={selectType}
              onChange={dateChange}
            />
          </div>
          <div className="card-body space-bottom-1">
            <Chart data={data} />
          </div>
        </div>
        <div className="my-4 text-right">
          Download: <a className="text-primary link-brand" href={`${window.location.origin}/blockBrowser/resources/chartStats/blockChainData/download?fileName=MRC20Txns&${downLoadUrl}`}>CSV Data</a> (Attribution Required) &nbsp;&nbsp;&nbsp;
        </div>
        <br />
      </div>
    </main>
  );
}
